import { useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { Actions, SubHeader } from 'components/shared/StandardModal';
import { SyncPatientToEhrAction } from 'models/SyncPatientToEhrAction';
import Workflow from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { VALUE_NAMES } from '../constants';
import RevealableSsn from '../RevealableSsn';

import EditableField from './EditableField';
import { MATRIXCARE, optionsForField, syncFieldErrors, syncFields } from './fields';
import { platformLabel } from './SyncPatientBody';

interface Props {
  workflow: Workflow;
  // The step being sent. Read for what the LAST dispatch submitted, so a fix-and-resend does not make
  // the coordinator retype a correction the vendor already rejected the referral over.
  action: SyncPatientToEhrAction;
  // Corrections plus the document selection, both staged on the dispatch. The corrections are NOT
  // written back to the referral: those names are declared inputs of the auto-checks, and writing them
  // back re-ran the denial list and the sex offender check (QA, 2026-08-18).
  onSend: (values: WorkflowValuesInput, outputs: WorkflowValuesInput) => Promise<unknown>;
  onClose: () => void;
  // Reopened to fix a field the vendor rejected — skip the document step and focus the MRN.
  startOnReview?: boolean;
}

const Form = styled.div<{ $hidden?: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'block')};
`;

const DocRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  cursor: pointer;
`;

const DocMeta = styled(BodySmall)`
  color: ${colors.black50};
`;

const SelectAll = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  text-decoration: underline;
  align-self: flex-start;
`;

const Note = styled(BodySmall)`
  color: ${colors.black50};
`;

const ErrorSummary = styled(BodySmall)`
  color: ${colors.red100};
`;

// A non-blocking caution (no files, or all files deselected). Amber, not red: the coordinator can
// still proceed — they may intend to upload to the EHR by hand.
const Warning = styled(BodySmall)`
  display: block;
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  color: ${colors.orangeExtraDark};
  background: ${colors.orange10};
`;

/**
 * Review-and-send, before a patient is written into a real EHR (OLIO-2846, delivery in OLIO-2733).
 *
 * Steps, in the screenshots' order: pick the documents, review the demographics, then confirm the
 * permanent EHR write. There is no stepper component in the repo, so this follows ReferralModal's house
 * pattern — the inactive step is HIDDEN rather than unmounted, so Back restores the selection and any
 * in-progress field edit instead of resetting them.
 *
 * The document step is MatrixCare-only: Resident.Document.Add delivers the selected PDFs after the
 * patient import (OLIO-2733). PCC has no document step yet, so a PCC sync skips it entirely — offering a
 * picker that delivers nothing would imply otherwise.
 */
export default function SyncPatientModal({ workflow, action, onSend, onClose, startOnReview = false }: Props) {
  const platform = workflow.ehrSync.platform;
  const platformName = platformLabel(platform);
  const fields = useMemo(() => syncFields(platform), [platform]);

  // The document chooser exists only for MatrixCare — Resident.Document.Add is the one path that
  // actually delivers files (OLIO-2733). PCC has no document step yet, so offering a picker there would
  // imply a delivery that does not happen; a PCC sync skips straight to review.
  const skipDocuments = platform !== MATRIXCARE;

  // Three steps for MatrixCare: pick documents, review demographics, then an explicit confirmation that
  // the sync writes a PERMANENT EHR record (OLIO-2733). PCC (and a `startOnReview` rejected-field fix)
  // skip the document step and land on review.
  const [step, setStep] = useState<'documents' | 'review' | 'confirm'>(
    startOnReview || skipDocuments ? 'review' : 'documents'
  );
  const [sending, setSending] = useState(false);

  // What the REFERRAL holds. The diff below is always taken against this, never against what is on
  // screen — that distinction is the whole reason a resend works: a correction inherited from the last
  // attempt still differs from the referral, so it is sent again rather than computing an empty diff
  // and silently reverting to the referral's value.
  const committed = useMemo(
    () => Object.fromEntries(fields.map((field) => [field.name, workflow.valueFor(field.name)?.value ?? ''])),
    [fields, workflow]
  );

  // What the screen opens with: the referral, overlaid with whatever the last dispatch submitted. So a
  // vendor rejection reopens showing the corrected data the coordinator already typed.
  const opening = useMemo(() => ({ ...committed, ...action.submittedDemographics }), [committed, action]);
  const [values, setValues] = useState<Record<string, string>>(opening);

  // The packet is already on the workflow — no fetch. Default to everything selected, since the
  // coordinator opened this to send the referral's documents.
  const [selectedDocs, setSelectedDocs] = useState<string[]>(() => workflow.attachments.map((a) => a.id));

  const errors = syncFieldErrors(values, platform);
  const blocked = Object.keys(errors).length > 0;

  const hasAttachments = workflow.attachments.length > 0;
  // Files exist but the coordinator cleared them all — a warning, not a block: they may intend to
  // upload manually. Distinct from "no files attached to the referral at all".
  const noneSelected = hasAttachments && selectedDocs.length === 0;

  const setValue = (name: string, value: string) => setValues((prev) => ({ ...prev, [name]: value }));

  const toggleDoc = (id: string) =>
    setSelectedDocs((prev) => (prev.includes(id) ? prev.filter((docId) => docId !== id) : [...prev, id]));

  const send = async () => {
    setSending(true);
    try {
      // Everything that differs from the REFERRAL, which includes a correction carried over from the
      // last attempt. Diffing against what the screen opened with instead would send nothing on an
      // unedited resend, and the vendor would receive the value the referral holds — the one it just
      // rejected.
      const changed = Object.fromEntries(Object.entries(values).filter(([name, value]) => value !== committed[name]));
      // PCC has no document step, so nothing was chosen — send an empty selection rather than the
      // all-selected default, which would record files the PCC path never delivers.
      await onSend(changed, { selected_attachment_ids: skipDocuments ? [] : selectedDocs });
      onClose();
    } catch {
      // Stay OPEN and swallow. The toast already carries the server's own sentence, and closing on a
      // failure is the specific bug this catch exists to prevent: the coordinator would be left
      // looking at a card that had not changed, with no way to tell whether the send had happened.
      // Swallowing rather than rethrowing keeps a failed send from surfacing as an unhandled
      // rejection — this handler IS how the failure is dealt with.
    } finally {
      // Reached on both paths; harmless after a close, and on failure it is what stops the button
      // spinning forever over an error the coordinator needs to act on.
      setSending(false);
    }
  };

  const title =
    step === 'documents'
      ? 'Select documents to send'
      : step === 'review'
        ? 'Review patient details'
        : `Confirm sync to ${platformName}`;

  return (
    // 560px explicitly: StandardModal defaults to flex-basis 480px with `flex: 0`, so it never grows
    // into its 560 max — and the label/value rows wrap at 480.
    <StandardModal title={title} onCancel={onClose} maxWidth='560px'>
      {!skipDocuments && (
        <Form $hidden={step !== 'documents'} data-testid='sync-step-documents'>
          {!hasAttachments ? (
            // AC: no files attached — the modal still opens and can proceed, but warns that files must
            // be uploaded to the EHR by hand.
            <Warning data-testid='sync-no-documents'>
              This referral has no documents to send. You&apos;ll need to upload any files to {platformName} manually.
            </Warning>
          ) : (
            <>
              <SelectAll
                type='button'
                onClick={() =>
                  setSelectedDocs(
                    selectedDocs.length === workflow.attachments.length ? [] : workflow.attachments.map((a) => a.id)
                  )
                }
                data-testid='sync-select-all'>
                {selectedDocs.length === workflow.attachments.length ? 'Select none' : 'Select all'}
              </SelectAll>
              {workflow.attachments.map((attachment) => (
                <DocRow key={attachment.id}>
                  <input
                    type='checkbox'
                    checked={selectedDocs.includes(attachment.id)}
                    onChange={() => toggleDoc(attachment.id)}
                    data-testid={`sync-doc-${attachment.id}`}
                  />
                  <BodySmall>{attachment.filename ?? 'Document'}</BodySmall>
                  {/* No file size: the workflow payload carries only id/filename/docType. Size is in
                    the manifest the platform records, which is where it is actually reported. */}
                  <DocMeta>{attachment.docType ?? ''}</DocMeta>
                </DocRow>
              ))}
              {/* AC: deselecting every file is allowed, but warned — the coordinator may mean to upload
                by hand, so this informs rather than blocks. */}
              {noneSelected && (
                <Warning data-testid='sync-none-selected'>
                  No files will be synced. You&apos;ll need to upload documents to {platformName} manually.
                </Warning>
              )}
            </>
          )}
          {platform === MATRIXCARE && hasAttachments && (
            <Note data-testid='sync-pdf-only'>
              Only PDF files can be sent to {platformName}; other file types are skipped and must be uploaded manually.
            </Note>
          )}
          <Actions>
            <Button variant='outline-primary' size='small' onClick={onClose}>
              Cancel
            </Button>
            <Button variant='primary' size='small' onClick={() => setStep('review')} data-testid='sync-next'>
              Next
            </Button>
          </Actions>
        </Form>
      )}

      <Form $hidden={step !== 'review'} data-testid='sync-step-review'>
        <SubHeader>Sending to {platformName}</SubHeader>

        {fields.map((field) => (
          <EditableField
            key={field.name}
            field={field}
            value={values[field.name] ?? ''}
            onChange={(value) => setValue(field.name, value)}
            options={optionsForField(field.name)}
            error={errors[field.name]}
            // Masked while reading, raw while editing — and NOT masked when it is invalid, because
            // masking a value you are asking someone to fix is the actual bug (the mask is what made a
            // redacted number invisible in the first place).
            renderValue={
              field.name === VALUE_NAMES.PATIENT_SSN && !errors[field.name]
                ? (value) => <RevealableSsn ssn={value} />
                : undefined
            }
          />
        ))}

        {platform === MATRIXCARE && (
          <Note>
            If this facility assigns medical record numbers by hand, turn it on in the patient-sync settings and Olio
            will generate one. There is nothing to enter here.
          </Note>
        )}

        {blocked && (
          <ErrorSummary data-testid='sync-blocked'>
            Correct the highlighted fields before sending to {platformName}.
          </ErrorSummary>
        )}

        <Actions>
          <Button
            variant='outline-primary'
            size='small'
            onClick={() => (startOnReview || skipDocuments ? onClose() : setStep('documents'))}
            data-testid='sync-back'>
            {startOnReview || skipDocuments ? 'Cancel' : 'Back'}
          </Button>
          <Button
            variant='primary'
            size='small'
            disabled={blocked}
            onClick={() => setStep('confirm')}
            data-testid='sync-review-next'>
            Next
          </Button>
        </Actions>
      </Form>

      <Form $hidden={step !== 'confirm'} data-testid='sync-step-confirm'>
        {/* AC: an explicit confirmation that the push is a PERMANENT EHR record. Load-bearing — this is
            the last stop before an irreversible write into a real chart system. */}
        <Warning data-testid='sync-permanent-warning'>
          Syncing creates a <strong>permanent record</strong> in {platformName} that cannot be undone.
        </Warning>
        {/* Documents are part of the sync only where they are actually delivered (MatrixCare). PCC has
            no document step, so the confirmation says nothing about files it will not send. */}
        {!skipDocuments && (
          <Note data-testid='sync-confirm-summary'>
            {noneSelected || !hasAttachments
              ? `No documents will be sent — you'll need to upload them to ${platformName} manually.`
              : `${selectedDocs.length} document${selectedDocs.length === 1 ? '' : 's'} will be sent to ${platformName}.`}
          </Note>
        )}
        <Actions>
          <Button
            variant='outline-primary'
            size='small'
            onClick={() => setStep('review')}
            data-testid='sync-confirm-back'>
            Back
          </Button>
          <Button
            variant='primary'
            size='small'
            disabled={blocked || sending}
            onClick={send}
            data-testid='sync-confirm-send'>
            {sending ? 'Sending…' : `Confirm and sync to ${platformName}`}
          </Button>
        </Actions>
      </Form>
    </StandardModal>
  );
}
